import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const input =
  "mt-1 h-9 w-full rounded-sm border border-slate-300 bg-white px-2.5 text-[13px] outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200";
const label = "label-xs mt-3 block";

const EMPTY = {
  company_name: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  country: "NL",
  tenant_role: "CUSTOMER_ADMIN",
  account_status: "INVITED",
  subscription_status: "TRIAL",
  plan_code: "imdash_pro",
  monthly_price: "",
  subscription_started_at: "",
  subscription_ends_at: "",
  trial_ends_at: "",
  internal_note: "",
  is_test_account: false,
};

export const AccountFormDialog = ({ plans, onClose, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = {
        ...form,
        monthly_price: form.monthly_price === "" ? null : Number(form.monthly_price),
        subscription_started_at: form.subscription_started_at || null,
        subscription_ends_at: form.subscription_ends_at || null,
        trial_ends_at: form.trial_ends_at || null,
      };
      const { data } = await api.post("/admin/accounts", payload);
      toast.success(
        data.invite_email_sent
          ? "Klant aangemaakt en uitnodiging verstuurd"
          : "Klant aangemaakt — e-mail kon niet worden verstuurd, gebruik de invite link",
      );
      onCreated(data);
    } catch (err) {
      setError(apiError(err));
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4">
      <form
        onSubmit={submit}
        data-testid="admin-create-account-form"
        className="panel w-full max-w-2xl p-5"
      >
        <h2 className="font-display text-lg font-semibold text-slate-900">Nieuwe ImDash-klant</h2>
        <p className="mt-1 text-[12px] text-slate-500">
          De klant ontvangt een uitnodiging en stelt zelf een wachtwoord in. Je bepaalt hier het
          abonnement en eventueel een afwijkende maandprijs.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className={label}>Bedrijfsnaam</label>
            <input
              required
              minLength={2}
              value={form.company_name}
              onChange={set("company_name")}
              data-testid="admin-company-input"
              className={input}
            />
          </div>
          <div>
            <label className={label}>E-mailadres</label>
            <input
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              data-testid="admin-email-input"
              className={input}
            />
          </div>
          <div>
            <label className={label}>Voornaam</label>
            <input value={form.first_name} onChange={set("first_name")} className={input} />
          </div>
          <div>
            <label className={label}>Achternaam</label>
            <input value={form.last_name} onChange={set("last_name")} className={input} />
          </div>
          <div>
            <label className={label}>Telefoonnummer</label>
            <input value={form.phone} onChange={set("phone")} className={input} />
          </div>
          <div>
            <label className={label}>Land</label>
            <input value={form.country} onChange={set("country")} className={input} />
          </div>
          <div>
            <label className={label}>Abonnement</label>
            <select
              value={form.plan_code}
              onChange={set("plan_code")}
              data-testid="admin-plan-select"
              className={input}
            >
              {(plans || []).map((plan) => (
                <option key={plan.code} value={plan.code}>
                  {`${plan.name} — € ${plan.base_price} / maand`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Afwijkende maandprijs (leeg = standaard)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.monthly_price}
              onChange={set("monthly_price")}
              data-testid="admin-price-input"
              className={input}
            />
          </div>
          <div>
            <label className={label}>Abonnementstatus</label>
            <select
              value={form.subscription_status}
              onChange={set("subscription_status")}
              data-testid="admin-subscription-select"
              className={input}
            >
              {["TRIAL", "ACTIVE", "PAYMENT_DUE", "PAST_DUE", "CANCELLED", "MANUAL"].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Accountstatus</label>
            <select
              value={form.account_status}
              onChange={set("account_status")}
              data-testid="admin-account-status-select"
              className={input}
            >
              {["INVITED", "ACTIVE", "PAUSED", "BLOCKED"].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Trial einddatum</label>
            <input
              type="date"
              value={form.trial_ends_at}
              onChange={set("trial_ends_at")}
              className={input}
            />
          </div>
          <div>
            <label className={label}>Einddatum abonnement</label>
            <input
              type="date"
              value={form.subscription_ends_at}
              onChange={set("subscription_ends_at")}
              className={input}
            />
          </div>
        </div>

        <label className={label}>Interne notitie</label>
        <textarea
          rows={2}
          value={form.internal_note}
          onChange={set("internal_note")}
          data-testid="admin-note-input"
          className={`${input} h-auto py-2`}
        />
        <label className="mt-3 flex items-center gap-2 text-[12px] text-slate-700">
          <input
            type="checkbox"
            checked={form.is_test_account}
            onChange={set("is_test_account")}
            data-testid="admin-test-account-checkbox"
          />
          Testaccount (buiten commerciële KPI's houden)
        </label>

        {error && (
          <div
            className="mt-3 rounded-sm border border-red-300 bg-red-50 px-2.5 py-2 text-[12px] text-red-800"
            data-testid="admin-create-error"
          >
            {error}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary" data-testid="admin-cancel-create">
            Annuleren
          </button>
          <button
            type="submit"
            disabled={busy}
            className="btn-primary"
            data-testid="admin-submit-create"
          >
            {busy ? "Aanmaken…" : "Klant aanmaken"}
          </button>
        </div>
      </form>
    </div>
  );
};
